import axios from 'axios';

const api = axios.create({
  baseURL: process.env.REACT_APP_API_URL || 'http://localhost:5001/archa-6b596/europe-west1',
});

// Retry configuration
const MAX_RETRIES = 3;
const RETRY_DELAY = 1000; // 1 second base delay

// Function to determine if error is retryable
const isRetryableError = (error) => {
  if (!error.response) {
    // Network errors are retryable
    return true;
  }

  const status = error.response.status;
  // Retry on server errors (5xx) and some client errors
  // 408: Request Timeout
  // 429: Too Many Requests
  // 500-599: Server errors
  return status === 408 || status === 429 || (status >= 500 && status < 600);
};

// Function to calculate retry delay with exponential backoff
const getRetryDelay = (retryCount) => {
  return RETRY_DELAY * Math.pow(2, retryCount);
};

// Request interceptor to set Content-Type only for non-FormData requests
api.interceptors.request.use((config) => {
  // Only set Content-Type if not already set and data is not FormData
  if (!config.headers['Content-Type'] && !(config.data instanceof FormData)) {
    config.headers['Content-Type'] = 'application/json';
  }
  // If data is FormData, remove Content-Type to let axios set it with boundary
  if (config.data instanceof FormData) {
    delete config.headers['Content-Type'];
  }
  // Initialize retry count
  config.retryCount = config.retryCount || 0;
  return config;
});

// Response interceptor for handling errors with retry logic
api.interceptors.response.use(
  (response) => {
    return response;
  },
  async (error) => {
    const config = error.config;

    // Check if we should retry
    if (config && config.retryCount < MAX_RETRIES && isRetryableError(error)) {
      config.retryCount += 1;
      const delay = getRetryDelay(config.retryCount - 1);

      console.log(`Request failed, retrying in ${delay}ms... (attempt ${config.retryCount}/${MAX_RETRIES})`);

      // Wait before retrying
      await new Promise(resolve => setTimeout(resolve, delay));

      // Retry the request
      return api(config);
    }

    // Handle non-retryable errors
    if (error.response) {
      console.error('API Error:', error.response.data);

      // Redirect to login if unauthorized
      if (error.response.status === 401) {
        window.location.href = '/login';
      }
    } else if (error.request) {
      console.error('Network Error:', error.request);
    } else {
      console.error('Error:', error.message);
    }
    return Promise.reject(error);
  }
);

export default api;
